import React, { useState, useEffect } from "react";
import {
    Box,
    Avatar,
    Typography,
    Button,
    TextField,
    Grid,
    Card,
    CardContent,
    CardActions,
    Divider,
    Chip,
    Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { updateUser, fetchUserFaqs, getCompanyInfo } from "../../api/api";
import { Edit, Person, Email, Phone, Cancel } from "@mui/icons-material";
import { getUserId } from "../../utils/cookieUtils";

const UserProfile = () => {
    const [isEditing, setIsEditing] = useState(false);
    // const [user, setUser] = useState({});
    const [userId, setUserId] = useState(null);

    const navigate = useNavigate(); // Redirect function
    const [userData, setUserData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        secondary_email: "",
        mobile_no: "",
        user_codes: "",
        profile_image: "",
    });
    const [backupData, setBackupData] = useState({});
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewImage, setPreviewImage] = useState("");
    const [companyInfo, setCompanyInfo] = useState(null);

    useEffect(() => {
        // Prefer cookie; also accept switch URL params so we never bounce to login mid-bootstrap
        const urlParams = new URLSearchParams(window.location.search);
        const userIdFromUrl = urlParams.get('user_id');
        const storedUserId = getUserId() || userIdFromUrl;

        if (!storedUserId) {
            navigate("/"); // Redirect only if userId is missing
        } else {
            setUserId(storedUserId);
        }
    }, [navigate]);

    useEffect(() => {
        if (userId) {
            fetchUserFaqs(userId)
                .then((response) => {
                    const user = response.data.user || {};
                    // ✅ Store user details; normalize nullable string fields to ""
                    setUserData({
                        ...user,
                        secondary_email: user.secondary_email ?? "",
                        mobile_no: user.mobile_no ?? "",
                    });
                    // setFaqs(response.data.faqs); // Uncomment if needed
                })
                .catch((error) => {
                    console.error("Error fetching Posts:", error);
                });
        }

        // Fetch company info for Beta badge
        const fetchCompanyInfo = async () => {
            try {
                const info = await getCompanyInfo();
                if (info.status && info.company) {
                    setCompanyInfo(info.company);
                }
            } catch (error) {
                console.error('Error fetching company info:', error);
            }
        };
        
        fetchCompanyInfo();
    }, [userId]);

    const handleEditToggle = () => {
        if (!isEditing) {
            // Enter edit mode → backup current secondary_email and mobile_no
            setBackupData({
                secondary_email: userData.secondary_email,
                mobile_no: userData.mobile_no,
            });
        } else {
            // Cancel → restore original values from backup
            setUserData((prev) => ({
                ...prev,
                secondary_email: backupData.secondary_email,
                mobile_no: backupData.mobile_no,
            }));
        }
        setIsEditing(!isEditing);
    };

    const handleInputChange = (field, value) => {
        setUserData((prevData) => ({
            ...prevData,
            [field]: value,
        }));
    };

    const handleSave = () => {
        const mobileRegex = /^\d{8}$/;

        if (userData.mobile_no && !mobileRegex.test(userData.mobile_no)) {
            Swal.fire({
                title: "Invalid Mobile Number",
                text: "Mobile number must be exactly 8 digits.",
                icon: "warning",
            });
            return;
        }

        const formData = new FormData();
        formData.append("first_name", userData.first_name ?? "");
        formData.append("last_name", userData.last_name ?? "");
        formData.append("email", userData.email ?? "");
        formData.append("secondary_email", userData.secondary_email ?? "");
        formData.append("mobile_no", userData.mobile_no ?? "");
        formData.append("user_codes", userData.user_codes ?? "");
        formData.append("status", userData.status ?? "");

        if (selectedImage) {
            formData.append("profile_image", selectedImage);
        }

        // ✅ Don't set Content-Type manually - axios will set it automatically with boundary for FormData
        updateUser(userId, formData)
            .then((response) => {
                setIsEditing(false);
                Swal.fire({
                    title: "Success!",
                    text: "Your profile has been updated successfully.",
                    icon: "success",
                });
            })
            .catch((error) => {
                console.error("Error updating user:", error);
                Swal.fire({
                    title: "Error!",
                    text: "Something went wrong. Please try again.",
                    icon: "error",
                });
            });
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#f9f9f9",
                py: 3,
            }}
        >
            <Box sx={{ maxWidth: 1200, mx: "auto", px: 3 }}>
                <Card
                    sx={{
                        mb: 4,
                        boxShadow:
                            "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                        borderRadius: 2,
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        {/* Avatar & User Info Header */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "center", sm: "flex-start" },
                                gap: 3,
                            }}
                        >
                            <Box sx={{ position: "relative" }}>
                                <Avatar
                                    src={
                                        previewImage
                                            ? previewImage
                                            : userData.profile_image
                                            ? `${
                                                  userData.profile_image
                                              }?v=${Date.now()}`
                                            : undefined
                                    }
                                    sx={{
                                        width: 96,
                                        height: 96,
                                        background:
                                            "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                                        color: "white",
                                        fontSize: "1.5rem",
                                        fontWeight: 600,
                                        border: "4px solid rgba(59, 130, 246, 0.2)",
                                    }}
                                >
                                    {!userData.profile_image &&
                                        `${userData.first_name?.[0]}${userData.last_name?.[0]}`}
                                </Avatar>

                                {isEditing && (
                                    <>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            id="avatar-upload"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    // ✅ Validate file size (5MB max, same as backend)
                                                    const maxSize = 5 * 1024 * 1024; // 5MB
                                                    if (file.size > maxSize) {
                                                        Swal.fire({
                                                            title: "File Too Large",
                                                            text: "Image size must be less than 5MB. Please choose a smaller image.",
                                                            icon: "warning",
                                                        });
                                                        e.target.value = ""; // Reset input
                                                        return;
                                                    }

                                                    // ✅ Validate file type
                                                    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                                                    if (!validTypes.includes(file.type)) {
                                                        Swal.fire({
                                                            title: "Invalid File Type",
                                                            text: "Please upload a valid image file (JPEG, PNG, GIF, or WebP).",
                                                            icon: "warning",
                                                        });
                                                        e.target.value = ""; // Reset input
                                                        return;
                                                    }

                                                    setSelectedImage(file);
                                                    setPreviewImage(
                                                        URL.createObjectURL(
                                                            file
                                                        )
                                                    );
                                                }
                                            }}
                                        />
                                        <label htmlFor="avatar-upload">
                                            <Tooltip title="Change profile picture">
                                                <Box
                                                    sx={{
                                                        cursor: "pointer",
                                                        position: "absolute",
                                                        bottom: 0,
                                                        right: 0,
                                                        bgcolor: "#1d4ed8",
                                                        borderRadius: "50%",
                                                        p: 0.5,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                    }}
                                                >
                                                    <Edit
                                                        sx={{
                                                            color: "#fff",
                                                            fontSize: 18,
                                                        }}
                                                    />
                                                </Box>
                                            </Tooltip>
                                        </label>
                                    </>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    flex: 1,
                                    textAlign: { xs: "center", sm: "left" },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                                <Typography
                                    variant="h4"
                                        sx={{ fontWeight: "bold" }}
                                >
                                    {userData.first_name} {userData.last_name}
                                </Typography>
                                    {/* Beta Badge - only show for trial accounts */}
                                    {companyInfo?.is_trial && (
                                        <Chip
                                            label={`BETA${companyInfo.days_remaining !== null && companyInfo.days_remaining >= 0 ? ` (${companyInfo.days_remaining} days left)` : ''}`}
                                            color="warning"
                                            size="small"
                                            sx={{
                                                fontWeight: 'bold',
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                                <Chip
                                    label={
                                        userData.user_codes === "EYDTU"
                                            ? "User"
                                            : "Admin"
                                    }
                                    size="small"
                                    sx={{
                                        background:
                                            "linear-gradient(to right, #3b82f6, #2563eb)",
                                        color: "white",
                                        fontWeight: 600,
                                        boxShadow:
                                            "0 2px 6px rgba(59,130,246,0.3)",
                                    }}
                                />
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: {
                                            xs: "column",
                                            sm: "row",
                                        },
                                        gap: 2,
                                        color: "text.secondary",
                                    }}
                                >
                                    {userData.email && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                            }}
                                        >
                                            <Email sx={{ fontSize: 16 }} />
                                            <Typography variant="body2">
                                                {userData.email || " "}
                                            </Typography>
                                        </Box>
                                    )}
                                    {userData.mobile_no && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                            }}
                                        >
                                            <Phone sx={{ fontSize: 16 }} />
                                            <Typography variant="body2">
                                                {userData.mobile_no || " "}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Profile Information Header */}
                        <Divider sx={{ mt: 2 }} />
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "space-between",
                                gap: 1,
                                mt: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Person sx={{ color: "primary.main" }} />
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Profile Information
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                onClick={handleEditToggle}
                                sx={{
                                    mt: 1,
                                    background:
                                        "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                                    borderRadius: 2,
                                    textTransform: "none",
                                    px: 3,
                                    "&:hover": {
                                        opacity: 0.9,
                                    },
                                }}
                            >
                                {isEditing ? (
                                    <Cancel sx={{ mr: 1, fontSize: 16 }} />
                                ) : (
                                    <Edit sx={{ mr: 1, fontSize: 16 }} />
                                )}
                            </Button>
                        </Box>

                        <Grid container spacing={2}>
                            {[
                                {
                                    label: "First Name",
                                    value: userData.first_name,
                                    field: "first_name",
                                    editable: false,
                                },
                                {
                                    label: "Last Name",
                                    value: userData.last_name,
                                    field: "last_name",
                                    editable: false,
                                },
                                {
                                    label: "Email",
                                    value: userData.email,
                                    field: "email",
                                    editable: false,
                                },
                                {
                                    label: "Secondary Email *",
                                    value: userData.secondary_email,
                                    field: "secondary_email",
                                    editable: true,
                                },
                                {
                                    label: "Mobile Number *",
                                    value: userData.mobile_no,
                                    field: "mobile_no",
                                    editable: true,
                                },
                            ].map(({ label, field, editable }) => (
                                <Grid item xs={12} key={field}>
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        sx={{ mb: 0.5 }}
                                    >
                                        {label}
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={userData[field] ?? ""}
                                        onChange={(e) =>
                                            handleInputChange(
                                                field,
                                                e.target.value
                                            )
                                        }
                                        InputProps={{
                                            readOnly: !editable || !isEditing,
                                        }}
                                        error={
                                            field === "mobile_no" &&
                                            (userData.mobile_no ?? "").trim() !== "" &&
                                            !/^\d{8}$/.test(userData.mobile_no)
                                        }
                                        helperText={
                                            field === "mobile_no" &&
                                            (userData.mobile_no ?? "").trim() !== "" &&
                                            !/^\d{8}$/.test(userData.mobile_no)
                                                ? "Mobile number must be exactly 8 digits"
                                                : !editable
                                                ? "This field cannot be changed"
                                                : ""
                                        }
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                transition: "all 0.2s",
                                                "&:focus-within": {
                                                    boxShadow:
                                                        "0 0 0 2px rgba(59, 130, 246, 0.2)",
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>

                    {/* Save Button */}
                    {isEditing && (
                        <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                sx={{
                                    background:
                                        "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                                    "&:hover": { opacity: 0.9 },
                                }}
                            >
                                Save Changes
                            </Button>
                        </CardActions>
                    )}
                </Card>
            </Box>
        </Box>
    );
};

export default UserProfile;
